import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Access, AccessRule, Department } from '@prisma/client';
import { Breadcrumb, Button, Form, Layout, Result, Select, Space, Spin, theme } from 'antd';

import { Auth, PageLayout, Picker } from '@app/web-components';

import { useFindWorker } from '../../../subscriptions/data';
import { useFindAccessRules } from '../../rules/data/access-rule';
import { useGetUserAccessRuleExceptions } from '../../rules/data/access-rule-exceptions';
import { ManageExceptionsForm } from '../forms/manage';

export type AccessRuleWithDetail = AccessRule & {
  access: Access;
  conflictAccess?: Access;
  department?: Department;
};

export const ManageUserExceptionsPage = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const { getSelectProps } = Picker.useWorkerPicker();
  const { user: loggedInUser } = Auth.useUserAuth();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [success, setSuccess] = useState(false);
  const user = Form.useWatch('user', form);

  const userQuery = useFindWorker(user?.value ?? '', { enabled: !!user?.value });

  const loggedInUserRules = useFindAccessRules<AccessRuleWithDetail>({
    where: { employeeType: userQuery.data?.employeeType, owners: { some: { userId: loggedInUser?.id } } },
    include: { access: true, conflictAccess: true, department: true },
  });

  const userExceptionsQuery = useGetUserAccessRuleExceptions(user?.value ?? '', {
    enabled: !!user?.value,
  });

  const onReset = () => {
    form.resetFields();
    setSuccess(false);
  };

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="../">Exceptions</Link> },
          { title: 'Manage User Exceptions' },
        ]}
      />
      <Layout.Content
        style={{
          padding: 24,
          margin: 'auto',
          height: '100%',
          width: '100%',
          maxWidth: 1200,
          minHeight: 0,
          background: colorBgContainer,
          overflow: 'auto',
        }}
      >
        <PageLayout.Title title="Manage User Exceptions" />

        {success && (
          <Result
            status="success"
            title="Successfully updated exceptions!"
            subTitle={`Exceptions for ${user?.label} were modified`}
            extra={[
              <Button type="primary" key="go to exceptions" onClick={() => navigate('../')}>
                Go To Exceptions
              </Button>,
              <Button key="reset" onClick={onReset}>
                Manage Another User's Exceptions
              </Button>,
            ]}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          hidden={success}
          onFieldsChange={(changedFields) =>
            changedFields.find((x) => x.name.includes('user')) && user?.value && userExceptionsQuery.refetch()
          }
        >
          <Form.Item label="User" name="user" required rules={[{ required: true, message: 'User is required' }]}>
            <Select {...getSelectProps()} />
          </Form.Item>
        </Form>
        {loggedInUserRules.isFetching ||
        userExceptionsQuery.isFetching ||
        userQuery.isFetching ||
        !loggedInUserRules.data ? (
          <Spin style={{ width: '100%' }} />
        ) : user && userQuery.data && !success ? (
          <ManageExceptionsForm
            loggedInUserRules={loggedInUserRules.data?.data ?? []}
            targetUserExceptions={userExceptionsQuery.data?.data ?? []}
            user={userQuery.data}
            onReset={onReset}
            onSuccess={() => setSuccess(true)}
          />
        ) : success ? undefined : (
          /* Action buttons displayed to mimic ManageExceptionsForm action button layout when user
           not selected. May not be required if the forms are merged into one, at a later time.
         */
          <Space>
            <Button type="primary" disabled>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
          </Space>
        )}
      </Layout.Content>
    </>
  );
};
